import { motion } from "framer-motion";

export function SectionLabel({ children, className = "" }) {
  return (
    <div className={`inline-flex items-center gap-3 ${className}`}>
      <span className="h-px w-8 bg-[#e01e37]" />
      <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#e01e37]">
        {children}
      </span>
    </div>
  );
}

export function Reveal({ children, delay = 0, y = 24, className = "" }) {
  return (
    <motion.div
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

const KEYWORDS = [
  "const", "let", "await", "async", "function", "return", "new", "import",
  "from", "export", "if", "else",
];

// Lightweight tokenizer for the code showcase. Keeps the design-system colors.
function highlight(line) {
  if (line.trim().startsWith("//")) {
    return [{ c: "text-white/30", v: line }];
  }
  const tokens = [];
  // split on strings first
  const regex = /("(?:[^"\\]|\\.)*"|`(?:[^`\\]|\\.)*`)/g;
  let lastIndex = 0;
  let match;
  const pushPlain = (text) => {
    // highlight keywords and numbers within plain text
    const parts = text.split(/(\b(?:const|let|await|async|function|return|new|import|from|export|if|else)\b|\b\d[\d_.]*\b)/g);
    parts.forEach((p) => {
      if (!p) return;
      if (KEYWORDS.includes(p)) tokens.push({ c: "text-[#e01e37]", v: p });
      else if (/^\d[\d_.]*$/.test(p)) tokens.push({ c: "text-amber-400/90", v: p });
      else tokens.push({ c: "text-white/80", v: p });
    });
  };
  while ((match = regex.exec(line)) !== null) {
    if (match.index > lastIndex) pushPlain(line.slice(lastIndex, match.index));
    tokens.push({ c: "text-emerald-400/90", v: match[0] });
    lastIndex = regex.lastIndex;
  }
  if (lastIndex < line.length) pushPlain(line.slice(lastIndex));
  return tokens;
}

export function CodeBlock({ filename = "cashfux.ts", method, endpoint, code = [] }) {
  return (
    <div className="border border-white/10 bg-[#0a0c12] accent-glow" data-testid="code-block">
      <div className="flex items-center justify-between border-b border-white/10 px-5 py-3">
        <span className="font-mono text-xs text-white/40">{filename}</span>
        <div className="flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 bg-white/15" />
          <span className="h-2.5 w-2.5 bg-white/15" />
          <span className="h-2.5 w-2.5 bg-[#e01e37]/70" />
        </div>
      </div>
      <div className="overflow-x-auto p-6 lg:p-7">
        <pre className="font-mono text-xs sm:text-[13px] leading-relaxed">
          <code>
            {code.map((line, i) => (
              <div key={i} className="flex">
                <span className="mr-5 w-4 select-none text-right text-white/15">{i + 1}</span>
                <span className="whitespace-pre">
                  {highlight(line).map((tok, j) => (
                    <span key={j} className={tok.c}>{tok.v}</span>
                  ))}
                </span>
              </div>
            ))}
          </code>
        </pre>
      </div>
      {(method || endpoint) && (
        <div className="flex items-center justify-between border-t border-white/10 px-5 py-3">
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/30">
            {method} {endpoint}
          </span>
          <span className="font-mono text-[10px] text-emerald-400/80">200 OK · 84ms</span>
        </div>
      )}
    </div>
  );
}
